// Service-mediated auth adapter. Calls per-product-line auth service wrappers
// (e.g. gaxl-iam-authentication-service, tc-identity-service) via configurable
// endpoint paths. The UI never talks to an IdP directly — token exchange is
// server-side within the auth service.

import { jwtDecode } from 'jwt-decode';
import type { Tenant, User } from '@growthaxl/web-foundation';
import pkceChallenge from 'pkce-challenge';
import type { AuthAdapter, AuthFlow, AuthIdentity } from './AuthAdapter';
import { AuthRefreshError } from './AuthRefreshError';
import type { ClaimMapping } from './claimResolver';
import { resolveIdentity } from './claimResolver';
import {
  AUTH_ACCESS_TOKEN_KEY,
  AUTH_CLIENT_ID_KEY,
  AUTH_PKCE_VERIFIER_KEY,
  AUTH_REFRESH_TOKEN_KEY,
  AUTH_SESSION_ID_KEY,
} from '../../constants/storageKeys';
import { AUTH_REFRESH_TOKEN_PATH } from '../../constants/apiPaths';

export {
  AUTH_ACCESS_TOKEN_KEY,
  AUTH_CLIENT_ID_KEY,
  AUTH_PKCE_VERIFIER_KEY,
  AUTH_REFRESH_TOKEN_KEY,
  AUTH_SESSION_ID_KEY,
} from '../../constants/storageKeys';

export interface AuthEndpoints {
  idpConfig?: string;
  session: string;
  logout: string;
  refresh?: string;
}

export interface ServiceAuthConfig {
  serviceUrl: string;
  endpoints: AuthEndpoints;
  flow?: AuthFlow;
  tenant?: string;
  claimMapping?: ClaimMapping;
  /**
   * When set the credentials login body uses the CMS gateway wire shape:
   * `{ tenant_code, email, password, timezone }` instead of `{ username, password }`.
   * The response must include `refresh_token` (no `session_id`).
   */
  tenantCode?: string;
}

interface IdpConfigResponse {
  client_id: string;
  login_url: string;
  flow?: AuthFlow;
}

interface IdpConfig {
  clientId: string;
  loginUrl: string;
  flow?: AuthFlow;
}

interface SessionResponse {
  access_token: string;
  /** Session-id wire (gaxl-iam / tc-identity direct). */
  session_id?: string;
  /** Token-pair wire (tc-gateway CMS). */
  refresh_token?: string;
  expires_in?: number;
}

export class ServiceAuthAdapter implements AuthAdapter {
  readonly name = 'service-auth';
  readonly flow: AuthFlow;
  private readonly cfg: ServiceAuthConfig;

  constructor(config: ServiceAuthConfig) {
    this.cfg = config;
    this.flow = config.flow ?? 'redirect';
  }

  private serviceBase(): string {
    return this.cfg.serviceUrl.replace(/\/$/, '');
  }

  private endpointUrl(path: string): string {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${this.serviceBase()}${normalized}`;
  }

  private tenantHeader(): Record<string, string> {
    const tenant = this.cfg.tenant;
    return tenant ? { 'x-tenant-id': tenant } : {};
  }

  private async fetchIdpConfig(tenantId?: string): Promise<IdpConfig> {
    const idpConfigPath = this.cfg.endpoints.idpConfig;
    if (!idpConfigPath) {
      throw new Error('idpConfig endpoint is required for redirect auth flow');
    }

    const tenant = tenantId ?? this.cfg.tenant ?? '';
    const url = `${this.endpointUrl(idpConfigPath)}?tenant_id=${encodeURIComponent(tenant)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`IdP config fetch failed: ${res.status}`);

    const data = (await res.json()) as IdpConfigResponse;
    return {
      clientId: data.client_id,
      loginUrl: data.login_url,
      flow: data.flow,
    };
  }

  async buildLoginUrl(redirectUri: string): Promise<string> {
    const idp = await this.fetchIdpConfig();
    const { code_verifier, code_challenge } = await pkceChallenge();
    sessionStorage.setItem(AUTH_PKCE_VERIFIER_KEY, code_verifier);
    sessionStorage.setItem(AUTH_CLIENT_ID_KEY, idp.clientId);

    const url = new URL(idp.loginUrl);
    url.searchParams.set('client_id', idp.clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'openid profile email');
    url.searchParams.set('code_challenge', code_challenge);
    url.searchParams.set('code_challenge_method', 'S256');
    return url.toString();
  }

  async handleCallback(callbackParams: URLSearchParams, redirectUri: string): Promise<void> {
    const code = callbackParams.get('code');
    if (!code) throw new Error('Missing authorization code in callback');

    const verifier = sessionStorage.getItem(AUTH_PKCE_VERIFIER_KEY) ?? '';
    const clientId = sessionStorage.getItem(AUTH_CLIENT_ID_KEY) ?? '';
    sessionStorage.removeItem(AUTH_PKCE_VERIFIER_KEY);

    const body = new URLSearchParams({
      code,
      client_id: clientId,
      code_verifier: verifier,
      redirect_uri: redirectUri,
    });

    const res = await fetch(this.endpointUrl(this.cfg.endpoints.session), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...this.tenantHeader(),
      },
      body,
    });
    if (!res.ok) throw new Error(`Session creation failed: ${res.status}`);

    const data = (await res.json()) as SessionResponse;
    this.storeSession(data);
  }

  async loginWithCredentials(username: string, password: string): Promise<void> {
    const body = JSON.stringify({
      username,
      password,
      ...(this.cfg.tenantCode && { tenant_code: this.cfg.tenantCode }),
      ...(this.cfg.tenantCode && { timezone: this.resolveTimezone() }),
    });

    const res = await fetch(this.endpointUrl(this.cfg.endpoints.session), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.tenantHeader(),
      },
      body,
    });

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) throw new Error('Invalid credentials');
      throw new Error(`Authentication failed: ${res.status}`);
    }

    const data = (await res.json()) as SessionResponse;
    if (!data.access_token) throw new Error('No access token in response');
    this.storeSession(data);
  }

  private resolveTimezone(): string {
    try {
      const raw = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      // Browsers/OS may still report legacy aliases that ZoneInfo rejects (e.g. Asia/Calcutta).
      const LEGACY_IANA: Record<string, string> = {
        'Asia/Calcutta': 'Asia/Kolkata',
        'Asia/Saigon': 'Asia/Ho_Chi_Minh',
        'Asia/Katmandu': 'Asia/Kathmandu',
        'Europe/Kiev': 'Europe/Kyiv',
      };
      return LEGACY_IANA[raw] ?? raw;
    } catch {
      return 'UTC';
    }
  }

  private storeSession(data: SessionResponse): void {
    sessionStorage.setItem(AUTH_ACCESS_TOKEN_KEY, data.access_token);
    if (data.session_id) sessionStorage.setItem(AUTH_SESSION_ID_KEY, data.session_id);
    if (data.refresh_token) sessionStorage.setItem(AUTH_REFRESH_TOKEN_KEY, data.refresh_token);
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem(AUTH_ACCESS_TOKEN_KEY);
  }

  /**
   * Returns the opaque server-side session credential.
   * For session-id wire: the `session_id` value.
   * For token-pair wire (CMS): the `refresh_token` value (used as session credential).
   */
  getSessionId(): string | null {
    return (
      sessionStorage.getItem(AUTH_SESSION_ID_KEY) ??
      sessionStorage.getItem(AUTH_REFRESH_TOKEN_KEY)
    );
  }

  getIdentity(): AuthIdentity | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const payload = jwtDecode<Record<string, unknown>>(token);
      const id = resolveIdentity(payload, this.cfg.claimMapping, this.cfg.tenant);
      const user: User = { id: id.userId, name: id.name, email: id.email, roles: id.roles };
      const tenant: Tenant = { id: id.tenant, name: id.tenant };
      return { user, tenant };
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    try {
      const payload = jwtDecode<{ exp?: number }>(token);
      if (payload.exp && payload.exp * 1000 < Date.now()) return false;
      return true;
    } catch {
      return false;
    }
  }

  async logout(): Promise<string | null> {
    console.info('[auth] logout initiated');
    const token = this.getAccessToken();
    const refreshToken = sessionStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
    const sessionId = sessionStorage.getItem(AUTH_SESSION_ID_KEY);
    let logoutRedirectUrl: string | null = null;

    try {
      if (token && sessionId) {
        // Session-id wire (OIDC / redirect flow): credentials in headers.
        // Preferred over token-pair wire because the server returns
        // logout_redirect_url which terminates the IDP SSO session.
        const res = await fetch(this.endpointUrl(this.cfg.endpoints.logout), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'x-session-id': sessionId,
            ...this.tenantHeader(),
          },
        });
        if (res.ok) {
          const data = (await res.json()) as { logout_redirect_url?: string };
          logoutRedirectUrl = data.logout_redirect_url ?? null;
        }
      } else if (refreshToken) {
        // Token-pair wire (CMS gateway / credentials flow): body carries refresh_token.
        await fetch(this.endpointUrl(this.cfg.endpoints.logout), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      }
    } catch {
      /* best-effort server logout */
    }

    this.clearSession();
    console.info('[auth] logout complete');
    return logoutRedirectUrl;
  }

  async refreshToken(): Promise<void> {
    const storedRefreshToken = sessionStorage.getItem(AUTH_REFRESH_TOKEN_KEY);

    if (storedRefreshToken) {
      // Token-pair wire (CMS gateway): POST body { refresh_token }.
      const refreshPath = this.cfg.endpoints.refresh ?? AUTH_REFRESH_TOKEN_PATH;
      const res = await fetch(this.endpointUrl(refreshPath), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefreshToken }),
      });

      if (!res.ok) {
        console.error(`[auth] token refresh failed: ${res.status}`);
        throw new AuthRefreshError(res.statusText || 'refresh rejected', res.status);
      }

      const data = (await res.json()) as SessionResponse;
      this.storeSession(data);
      console.info('[auth] token refreshed');
      return;
    }

    // Session-id wire: credentials in headers.
    const token = this.getAccessToken();
    const sessionId = sessionStorage.getItem(AUTH_SESSION_ID_KEY);

    if (!token || !sessionId) {
      throw new AuthRefreshError('No session to refresh');
    }

    const refreshPath = this.cfg.endpoints.refresh ?? AUTH_REFRESH_TOKEN_PATH;
    const userId = this.getIdentity()?.user.id;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'x-session-id': sessionId,
      ...this.tenantHeader(),
    };
    if (userId) headers['x-user-id'] = userId;

    const res = await fetch(this.endpointUrl(refreshPath), {
      method: 'POST',
      headers,
    });

    if (!res.ok) {
      console.error(`[auth] token refresh failed: ${res.status}`);
      throw new AuthRefreshError(res.statusText || 'refresh rejected', res.status);
    }

    const data = (await res.json()) as SessionResponse;
    this.storeSession(data);
    console.info('[auth] token refreshed');
  }

  clearSession(): void {
    sessionStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(AUTH_SESSION_ID_KEY);
    sessionStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(AUTH_PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(AUTH_CLIENT_ID_KEY);
  }

  isTokenNearExpiry(thresholdSeconds = 60): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      const payload = jwtDecode<{ exp?: number }>(token);
      if (!payload.exp) return false;
      return payload.exp * 1000 - Date.now() < thresholdSeconds * 1000;
    } catch {
      return false;
    }
  }
}
